import { useMemo, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { IconType } from '@factorialco/f0-react'
import { Menu } from '@factorialco/f0-react/dist/experimental'
import {
  FileSigned,
  Folder,
  Money,
  MoneyBag,
  People,
  Rocket,
  Wallet,
} from '@factorialco/f0-react/icons/app'
import { ExpenseDetailPageQuery, ExpensesSettingsPageQuery } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import Accounting from 'modules/expenses/pages/settings/ExpensePolicy/Accounting'
import CategorySettings from 'modules/expenses/pages/settings/ExpensePolicy/CategorySettings'
import DigitalCertifiedDocuments from 'modules/expenses/pages/settings/ExpensePolicy/DigitalCertifiedDocuments'
import DiscoverMore from 'modules/expenses/pages/settings/ExpensePolicy/DiscoverMore'
import ExpensesApprovalWorkflow from 'modules/expenses/pages/settings/ExpensePolicy/ExpensesApprovalWorkflow'
import FormsRequirements from 'modules/expenses/pages/settings/ExpensePolicy/FormsRequirements'
import Rates from 'modules/expenses/pages/settings/ExpensePolicy/Rates'
import SpendingGroups from 'modules/expenses/pages/settings/ExpensePolicy/SpendingGroups'

type ExpensesSignedDocumentsTerm = NonNullable<
  NonNullable<ExpenseDetailPageQuery['expenses']['signedDocumentsTermsConnection']>['nodes']
>[number]

type ExpenseCategory = ExpensesSettingsPageQuery['finance']['expenseCategories']['nodes'][number]

const PRIORITY_CATEGORY_IDENTIFIERS = ['accommodation', 'restaurants', 'taxis', 'training']

type MenuItemConfig = {
  id: string
  label: string
  icon: IconType
}

type MenuSectionConfig = {
  id: string
  title: string
  items: MenuItemConfig[]
}

interface SectionContentProps {
  canEditApprovers: boolean
  hasPerDiemsFeature: boolean
  hasNewPerDiemFeature: boolean
  hasExpensesGroupsFeature: boolean
  isExpensesGroupsEnabled: boolean
  hasExpensesFormCustomizationFeature: boolean
  hasLedgerAccountFeature: boolean
  canSeeLedgerAccounts: boolean
  signedDocumentsTerms: ExpensesSignedDocumentsTerm[]
  hasReportOnBehalfFeature: boolean
  expenseCategories: ExpenseCategory[]
}

const SectionContent = ({
  selectedItemId,
  ...props
}: SectionContentProps & { selectedItemId: string }) => {
  switch (selectedItemId) {
    case 'approval_workflows':
      return <ExpensesApprovalWorkflow canEditApprovers={props.canEditApprovers} />
    case 'forms_requirements':
      return (
        <FormsRequirements
          hasPerDiemsFeature={props.hasPerDiemsFeature}
          hasExpensesFormCustomizationFeature={props.hasExpensesFormCustomizationFeature}
        />
      )
    case 'rates':
      return (
        <Rates
          hasPerDiemsFeature={props.hasPerDiemsFeature}
          hasNewPerDiemFeature={props.hasNewPerDiemFeature}
        />
      )
    case 'spending_groups':
      return (
        <SpendingGroups
          hasExpensesGroupsFeature={props.hasExpensesGroupsFeature}
          isExpensesGroupsEnabled={props.isExpensesGroupsEnabled}
        />
      )
    case 'digital_certified_documents':
      return <DigitalCertifiedDocuments signedDocumentsTerms={props.signedDocumentsTerms} />
    case 'accounting':
      return (
        <Accounting
          hasLedgerAccountFeature={props.hasLedgerAccountFeature}
          canSeeLedgerAccounts={props.canSeeLedgerAccounts}
        />
      )
    case 'discover_more':
      return <DiscoverMore hasReportOnBehalfFeature={props.hasReportOnBehalfFeature} />
    default: {
      const category = props.expenseCategories.find(
        (cat) => `category_${cat.id}` === selectedItemId
      )
      if (category) {
        return <CategorySettings categoryLabel={category.label} />
      }
      return null
    }
  }
}

const categoryToMenuItem = (category: ExpenseCategory): MenuItemConfig => ({
  id: `category_${category.id}`,
  label: category.label,
  icon: Folder,
})

const ExpensePolicy = (sectionContentProps: SectionContentProps) => {
  const { canEditApprovers, expenseCategories } = sectionContentProps
  const [selectedItemId, setSelectedItemId] = useState(
    canEditApprovers ? 'approval_workflows' : 'forms_requirements'
  )

  const visibleCategoryItems = useMemo(
    () =>
      expenseCategories
        .filter((cat) => PRIORITY_CATEGORY_IDENTIFIERS.includes(cat.identifier))
        .map(categoryToMenuItem),
    [expenseCategories]
  )

  const generalSettingsSection = useMemo<MenuSectionConfig>(() => {
    const items: MenuItemConfig[] = []

    if (canEditApprovers) {
      items.push({
        id: 'approval_workflows',
        label: i18n.t('expenses_v2.settings.expense_policy.approval_workflows'),
        icon: Wallet,
      })
    }

    // TODO: cards
    items.push(
      {
        id: 'forms_requirements',
        label: i18n.t('expenses_v2.settings.forms.title'),
        icon: FileSigned,
      },
      { id: 'rates', label: i18n.t('expenses_v2.settings.rate_configurations.title'), icon: Money },
      {
        id: 'spending_groups',
        label: i18n.t('expenses_v2.settings.expenses_groups.title'),
        icon: People,
      }
    )

    return {
      id: 'general_settings',
      title: i18n.t('expenses_v2.settings.expense_policy.general_settings'),
      items,
    }
  }, [canEditApprovers])

  const categoriesSection = useMemo<MenuSectionConfig>(
    () => ({
      id: 'categories',
      title: i18n.t('expenses_v2.settings.expense_policy.categories'),
      items: visibleCategoryItems,
    }),
    [visibleCategoryItems]
  )

  const otherSettingsSection = useMemo<MenuSectionConfig>(
    () => ({
      id: 'other_settings',
      title: i18n.t('expenses_v2.settings.expense_policy.other_settings'),
      items: [
        {
          id: 'digital_certified_documents',
          label: i18n.t('expenses.settings.legal_compliance.title_v2'),
          icon: FileSigned,
        },
        {
          id: 'accounting',
          label: i18n.t('expenses_v2.settings.accounting.title'),
          icon: MoneyBag,
        },
        {
          id: 'discover_more',
          label: i18n.t('expenses_v2.settings.discover_more_features.title'),
          icon: Rocket,
        },
        //TODO: policy_document
      ],
    }),
    []
  )

  const menuTree = useMemo(() => {
    const sections = [generalSettingsSection]
    if (visibleCategoryItems.length > 0) {
      sections.push(categoriesSection)
    }
    sections.push(otherSettingsSection)
    return sections
  }, [generalSettingsSection, categoriesSection, otherSettingsSection, visibleCategoryItems.length])

  return (
    <Box flexDirection="row" height="full" width="full">
      <Box
        padding="s4"
        paddingBottom="s16"
        overflowX="hidden"
        height="full"
        border={{ all: { width: 's1', style: 'solid', color: 'grey400' } }}
        borderRadius={{ all: 'abs008' }}
      >
        <Menu
          tree={menuTree.map((section) => ({
            id: section.id,
            title: section.title,
            items: section.items.map((item) => ({
              id: item.id,
              label: item.label,
              icon: item.icon,
              onClick: () => setSelectedItemId(item.id),
            })),
          }))}
        />
      </Box>
      <SectionContent selectedItemId={selectedItemId} {...sectionContentProps} />
    </Box>
  )
}

export default ExpensePolicy
